import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsAccounts } from '~/stores/softswiss/accounts';
import { useStoreSsPaymentMethods } from '~/stores/softswiss/paymentMethods';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreSsWithdrawMethods } from '~/stores/softswiss/withdrawMethods';
import { useStoreCurrencyMenu } from '~/stores/strapi/currencyMenu';
import { useStoreModalDeposit } from '~/stores/strapi/modals/deposit';
import { useStorePaymentAlerts } from '~/stores/strapi/paymentAlerts';
import { useStorePaymentSystemImages } from '~/stores/strapi/paymentSystemImages';
import type { PaymentMethodFiatExtendedInterface } from '~/types/paymentMethodFiatExtended.interface';

export const usePaymentCommon = (storeType: 'deposit' | 'withdraw') => {
  const route = useRoute();
  const storeRoot = useStoreRoot();
  const storeSsPlayer = useStoreSsPlayer();
  const platform = usePlatform();
  const storeSsAccounts = useStoreSsAccounts();
  const storeCurrencyMenu = useStoreCurrencyMenu();
  const storePaymentAlerts = useStorePaymentAlerts();
  const storeModalDeposit = useStoreModalDeposit();
  const storePaymentSystemImages = useStorePaymentSystemImages();
  const storeMethods =
    storeType === 'deposit' ? useStoreSsPaymentMethods() : useStoreSsWithdrawMethods();

  const playerCurrency = computed(() => storeSsPlayer.playerCurrency || 'BTC');

  const isMethodsLoading = ref(true);
  const isWaitingCurrencyChange = ref(false);
  const currencyChanged = ref('');

  const isFiatCardsFlow = computed(() => storeMethods.selectedMethodType === 'cards');
  const isCryptoFlow = computed(() => storeMethods.selectedMethodType === 'crypto');

  /**
   * Getter that returns selected currency object
   * with player currency or new selected currency when new payments methods are loading
   */
  const selectedBalance = computed(() => {
    const currency = storeSsPlayer.playerCurrency;
    const selected =
      storeSsAccounts.balanceFormatted.find((cur) => cur.currency === currency) || null;

    if (selected) {
      selected.icon_url = storeCurrencyMenu.activeCurrencies.find(
        (ac) => ac.currencyCode === selected.currency
      )?.currencyIcon.url;
    }

    return selected;
  });

  const resetAllSsPaymentMethodsState = () => {
    storeMethods.resetSelectedMethod();
    storeMethods.resetPaymentPlan();
    storeMethods.resetErrorState();
  };

  const paymentAlertText = computed(() => {
    const alert = storePaymentAlerts.data.find(
      (item) => item.methodID === storeMethods.selectedMethod?.id
    );

    if (alert) {
      return alert.text;
    }

    return null;
  });

  const paymentAlertHeaderTitle = computed(() => {
    const alert = storePaymentAlerts.data.find(
      (item) => item.methodID === storeMethods.selectedMethod?.id
    );

    if (alert) {
      return alert.title;
    }

    return null;
  });

  const attentionBannerText = computed(() => {
    const restrictions = storeMethods.selectedMethod?.termsOfService?.restrictions;
    const currency = restrictions?.amountCurrencyCode || playerCurrency.value;

    const alertObject = storeModalDeposit.currencyAlert.find(
      (item) => item.alertCurrency === currency
    );

    return (
      alertObject?.alertMessage.replace(
        '{min_value_from_paymentslib}',
        `${restrictions?.minAmountValue} ${restrictions?.amountCurrencyCode}`
      ) || ''
    );
  });

  const isCryptoCurrency = computed(
    () =>
      storeCurrencyMenu.currencies.find((item) => item.currencyCode === playerCurrency.value)
        ?.crypto
  );

  const methodLogo = computed(() => {
    const methodLogoId = storeMethods.selectedMethodExtended?.methodLogoId;

    if (methodLogoId) {
      return storePaymentSystemImages.find(methodLogoId)?.paymentProviderLogo;
    }

    return null;
  });

  const setDefaultMethod = async () => {
    // route
    const paymentMethodLogoId = ref(route.query.paymentMethodLogoId);
    if (paymentMethodLogoId.value) {
      const matchingMethod = storeMethods.ssPaymentMethods.find(
        (method) => method.methodLogoId === paymentMethodLogoId.value
      );

      if (matchingMethod) {
        const methodIndex = storeMethods.ssPaymentMethods.indexOf(matchingMethod);
        storeMethods.setSelectedMethod(methodIndex);
      } else {
        storeMethods.setSelectedMethod(0);
      }
    } else {
      storeMethods.setSelectedMethod(0);
    }

    if (storeType === 'deposit' || storeType === 'withdraw') {
      await storeMethods.loadPaymentPlan(
        playerCurrency.value,
        storeMethods.selectedMethod?.id || ''
      );
    } else if (platform.isDesktop || platform.isTablet || isCryptoCurrency.value) {
      const playerCurrency = storeSsPlayer.playerCurrency || 'BTC';

      if (storeMethods.selectedMethod?.savedProfiles.length) {
        await storeMethods.loadPaymentPlanSavedMethod({
          currency: playerCurrency,
          methodID: storeMethods.selectedMethod?.id || '',
          savedMethod: { id: storeMethods.selectedMethod?.savedProfiles[0].id },
        });
      } else {
        await storeMethods.loadPaymentPlan(playerCurrency, storeMethods.selectedMethod?.id || '');
      }
    }
  };

  const addCurrency = async (currency: string) => {
    isWaitingCurrencyChange.value = true;
    isMethodsLoading.value = true;

    await storeRoot.setUserCurrency(currency);
    await storeSsAccounts.loadPlayerBalance({ force: true });
    isWaitingCurrencyChange.value = false;

    await storeMethods.loadMethods(currency);
    resetAllSsPaymentMethodsState();
    setDefaultMethod();

    isMethodsLoading.value = false;
  };

  const changeCurrency = async (playerCurrency: string) => {
    currencyChanged.value = playerCurrency;
    isWaitingCurrencyChange.value = true;
    isMethodsLoading.value = true;

    await storeRoot.setUserCurrency(playerCurrency);
    isWaitingCurrencyChange.value = false;
    currencyChanged.value = '';

    await storeMethods.loadMethods(playerCurrency);
    resetAllSsPaymentMethodsState();
    setDefaultMethod();

    isMethodsLoading.value = false;
  };

  const reload = async () => {
    isMethodsLoading.value = true;

    await storeMethods.loadMethods(storeRoot.userCurrency);
    resetAllSsPaymentMethodsState();
    setDefaultMethod();

    isMethodsLoading.value = false;
  };

  const parsedSystemImages = (method: PaymentMethodFiatExtendedInterface) => {
    const item = storePaymentSystemImages.find(method.methodLogoId);

    if (item?.paymentProviderLogo) {
      method.payment_provider_logo = item.paymentProviderLogo;
    }

    if (item?.recomendedForCurrencies && storeSsPlayer.playerCurrency) {
      const currencies = item.recomendedForCurrencies.split('\n');

      if (currencies.includes(storeSsPlayer.playerCurrency)) {
        method.recomended = true;
      }
    }

    return method;
  };

  const changePaymentMethod = (
    id: string,
    propsMethod: PaymentMethodFiatExtendedInterface[],
    savedMethodID?: string
  ) => {
    if (id === storeMethods.selectedMethod?.id && !savedMethodID) {
      return;
    }

    storeMethods.resetPaymentPlan();
    storeMethods.resetErrorState();
    const index = propsMethod.findIndex((method) => method.id === id);
    storeMethods.setSelectedMethod(index);

    const playerCurrency = storeSsPlayer.playerCurrency || 'BTC';
    if (savedMethodID) {
      storeMethods.loadPaymentPlanSavedMethod({
        methodID: id,
        currency: playerCurrency,
        savedMethod: { id: savedMethodID },
      });
    } else {
      storeMethods.loadPaymentPlan(playerCurrency, id);
    }
  };

  const getMinText = (method: PaymentMethodFiatExtendedInterface) => {
    return `${storeModalDeposit.min} ${method.termsOfService.restrictions.minAmountValue} ${method.termsOfService.restrictions.amountCurrencyCode}`;
  };

  const init = async () => {
    isMethodsLoading.value = true;
    storePaymentAlerts.loadAlerts();

    if (!storeSsPlayer.playerId) {
      await storeSsPlayer.load();
    }

    const playerCurrency = storeSsPlayer.playerCurrency || 'BTC';

    if (storeType === 'withdraw') {
      await storeSsAccounts.loadAllPlayerBalance();
    }

    await storeMethods.loadMethods(playerCurrency);

    setDefaultMethod();
    isMethodsLoading.value = false;
  };

  return {
    selectedBalance,
    playerCurrency,
    resetAllSsPaymentMethodsState,
    parsedSystemImages,
    paymentAlertText,
    paymentAlertHeaderTitle,
    isCryptoCurrency,
    setDefaultMethod,
    attentionBannerText,
    isMethodsLoading,
    isWaitingCurrencyChange,
    isFiatCardsFlow,
    isCryptoFlow,
    addCurrency,
    changeCurrency,
    init,
    changePaymentMethod,
    getMinText,
    methodLogo,
    currencyChanged,
    reload,
  };
};
